/**
 * The usage counter (BRIEF_USAGE_INDEXNOW.md §1): which tool was used and which formats were
 * involved, and nothing else.
 *
 * Conversion runs in the browser, so the server never hears about it. This module tells the
 * edge Worker's `POST /e` that a file was loaded, that one failed to load, that a result was
 * downloaded or copied, or that a page was left with a file loaded and no result: the event, the
 * tool's slug and a format from a fixed list (for a copy, how it was copied), plus `euc-kr` when
 * a download was saved in it and `repeat` when the result is already in the homepage's total.
 * The body is built here and only here, from those values alone. No file name, no contents, no
 * text from the file or the clipboard, no ids, no timestamps, ever.
 *
 * Separate from `lib/events.ts` on purpose. That one queues events for the app server; this is
 * a beacon the edge answers itself, so a counted click never wakes a container.
 *
 * NOTHING HERE CAN AFFECT THE PAGE. Every storage read and the beacon sit in try/catch, input
 * outside the lists is dropped rather than sent, and a browser without `sendBeacon` sends
 * nothing. The terms page says conversion never depended on us, and that stays true.
 */

import type { SaveEncoding } from './save-encoding.ts';

/** Same-origin, so the CSP's `connect-src 'self'` already allows it. */
export const USAGE_ENDPOINT = '/e';

/**
 * The `localStorage` key that turns counting off in this browser. The stats page sets it to
 * '1', so the founder's own checks stay out of the numbers.
 */
export const NO_STATS_FLAG = 'tw-no-stats';

/**
 * The five a tool page sends: a file loaded, one that failed to load, a result downloaded, a
 * result copied (lib/copies.ts; its format is how, from COPY_HOWS, not a file format), and
 * `no_result`: the page view ended with a file loaded and nothing downloaded or copied
 * (`endPageView`), sent with the format of the last file loaded. Then the microphone panel's
 * five (SEO_BRIEF_08 section 2.6, lib/mic-panel.ts): a recording started, sent to be
 * transcribed, or saved; live typing switched on, or its text copied (its Copy button, or text
 * selected in the box and copied). A recording's format is the one it was made in
 * (`recordingFormat`), live typing's is `txt`. Then the two PDF features the founder watches
 * (section 7.6):
 *   pdf_text_download     a PDF saved on /txt-to-pdf or /text-to-pdf
 *   pdf_layout_text_only  a transcript's PDF saved in the Text only layout (/t/{id}/pdf)
 * Then `page_time`: how long a page was on screen. Its body is not a tool's (a path and seconds,
 * lib/page-time.ts), so `usageBody` never builds one.
 * Then `example`: one of the homepage's three examples opened, and which (SEO_BRIEF_11 section 6,
 * lib/examples.ts, EXAMPLES below).
 * Then the eight of SEO_BRIEF_09 section 2.7 (FIELD_EVENTS below): translation, subtitle
 * translation, summaries, the caption generator, the language model's limit and live captions.
 * Then `share`: a transcript page's link shared, and how (SEO_BRIEF_11 section 5, lib/share.ts).
 * Then `guide_open`: a tool page opened from a "how to transcribe" guide, and which
 * (SEO_BRIEF_12 section 8, lib/guide-links.ts, GUIDES below). Then the live voice translator's
 * four (SEO_BRIEF_10 section 2.6). Last, `rate`: the optional rating pop-up's 1 to 5 stars
 * (lib/rating-prompt.ts, RATINGS below). Each event with fields carries its own short ones
 * (`UsageFields`), checked here and again by the Worker.
 * The Worker's USE_EVENTS (deploy/cloudflare-worker/worker.js) is this list, in this order, and
 * web/test/usage.test.mjs holds the two equal.
 */
export const USAGE_EVENTS = [
  'loaded',
  'failed',
  'downloaded',
  'copied',
  'no_result',
  'record_start',
  'record_transcribe',
  'record_download',
  'live_start',
  'live_copy',
  'live_save',
  'pdf_text_download',
  'pdf_layout_text_only',
  'page_time',
  'example',
  'translate_start',
  'translate_done',
  'subtitle_translate',
  'summary_start',
  'summary_cache_hit',
  'caption_generate',
  'gemini_limit_hit',
  'live_captions_start',
  'share',
  'guide_open',
  'live_translate_start',
  'live_translate_sentence',
  'live_translate_limit',
  'live_translate_download',
  'rate',
] as const;
export type UsageEvent = (typeof USAGE_EVENTS)[number];
/** The time-on-page beacon's event (lib/page-time.ts), one of USAGE_EVENTS. */
export const PAGE_TIME_EVENT = 'page_time' satisfies UsageEvent;
/** The microphone panel's events, a subset of USAGE_EVENTS. */
export type MicEvent = Extract<
  UsageEvent,
  | 'record_start'
  | 'record_transcribe'
  | 'record_download'
  | 'live_start'
  | 'live_copy'
  | 'live_save'
>;

/**
 * Every format the Worker counts. The read and write formats the library ships, `zip` for an
 * archive, `sup` and `idx` for the picture subtitles the OCR pages read (`idx` is VobSub, the
 * .idx and .sub pair), the video and audio a page burns into or sends for transcription
 * (`mediaFormat`), and `unknown` for a file that is none of them.
 */
export const USAGE_FORMATS = [
  'srt',
  'vtt',
  'ass',
  'ssa',
  'smi',
  'sub',
  'mpl2',
  'txt',
  'lrc',
  'lrc-enhanced',
  'sbv',
  'json',
  'ttml',
  'scc',
  'ebu-stl',
  'pdf',
  'docx',
  'zip',
  'sup',
  'idx',
  'mp4',
  'mov',
  'mkv',
  'webm',
  'video',
  'mp3',
  'm4a',
  'wav',
  'audio',
  'unknown',
] as const;
export type UsageFormat = (typeof USAGE_FORMATS)[number];

/**
 * How a result was copied, sent as a `copied` beacon's format in place of a file format: with its
 * Copy button, or as text selected in it and copied with the keyboard or the menu.
 */
export const COPY_HOWS = ['button', 'selection'] as const;
export type CopyHow = (typeof COPY_HOWS)[number];

/** A tool page's own path slug. The Worker drops anything else. */
export const TOOL_SLUG = /^[a-z0-9-]{1,48}$/;

/* ── EVENTS WITH FIELDS (SEO_BRIEF_09 section 2.7) ─────────────────────────
 *
 * `countUse(event, tool, format, extra)`: `extra` is a flat object of the event's own fields,
 * exactly those (`FIELD_EVENTS`), each a short code from a closed list or a number. It goes in
 * the body as `fields`, built key by key in the list's order, so nothing a caller adds rides
 * along; a missing, extra or off-list field drops the whole event. The Worker checks the same
 * lists (and the target against Whisper's languages) and counts each event per day, tool and
 * field values.
 *
 *   translate_start      target, engine, source,  a translation asked for, and how: the
 *                        trigger                  "Translate to English" button or a picker
 *   translate_done       target, engine           a translation ready
 *   subtitle_translate   target, cues, format     a subtitle file translated (cues: how many)
 *   summary_start        source                   a summary asked for
 *   summary_cache_hit    (none)                   a summary served from the cache
 *   caption_generate     platform                 social captions asked for
 *   gemini_limit_hit     feature, scope           the language model's daily limit was met
 *   live_captions_start  lang                     live captions switched on (a BCP 47 tag)
 *
 * The live voice translator's (SEO_BRIEF_10 section 2.6: /voice-translator, /live-translator,
 * /conversation-translator and the ten translated pages). `source` and `target` are the two
 * languages as codes (TARGET_CODE: 'es', 'zh', 'zh-Hant'); here `source` is a language, not where
 * the words came from, and `engine` is one of LIVE_TRANSLATE_ENGINES, not TRANSLATE_ENGINES
 * (EVENT_FIELD_CHECKS). The format is `txt`: what the translator gives back is text.
 *
 *   live_translate_start     mode, source, target    the first sentence of a session in a mode
 *   live_translate_sentence  engine, source,         sentences translated, by what, and how
 *                            target, sentences       many: 1 to LIVE_SENTENCE_BATCH
 *   live_translate_limit     scope                   the live translator met the model's limit
 *   live_translate_download  (none)                  the session saved as a .txt file
 *
 * The sentences are counted in batches, never a beacon each (lib/live-translator/
 * sentence-count.ts): the Worker adds `sentences` to the day's count.
 *
 * SHARING (SEO_BRIEF_11 section 5). The transcript page's Share button (lib/share.ts) sends one
 * `share` per link shared, tool `transcript`, format `txt` (the page's other events' format):
 *
 *   share                    method                  how: the device's share sheet, Copy link,
 *                                                    WhatsApp, Telegram, X or email
 *
 * RATINGS (the founder, 2026-10-30). The optional pop-up after a copy or a download
 * (lib/rating-prompt.ts) sends one `rate`, format `unknown` (a rating is of the tool, not of a
 * file):
 *
 *   rate                     stars, after, page      1 to 5 stars, after a copy or a download,
 *                                                    on this page (`location.pathname`)
 *
 * The Worker keeps it as a count by day, tool, page and stars, one per visitor, tool and day.
 *
 * EXAMPLES (SEO_BRIEF_11 section 6). The homepage's "Try an example" buttons (lib/examples.ts)
 * send one `example` each time an example is opened, tool `home`, format `unknown` (a stored
 * sample is no file of the visitor's):
 *
 *   example                  which                   the English talk, the Spanish clip in
 *                                                    English, or the YouTube video's summary
 *
 * GUIDES (SEO_BRIEF_12 section 8). A tap on a link to a tool page from a "how to transcribe"
 * guide's copy, steps or box (lib/guide-links.ts) sends one `guide_open` per tool page and page
 * view, tool the guide's own slug (what its box's events are counted under too), format
 * `unknown` (no file is involved):
 *
 *   guide_open               guide, tool             the guide (GUIDE_NAMES) and the tool page
 *                                                    opened from it, by its slug (TOOL_SLUG)
 */

/** Who translated: Whisper large-v3 (into English), the language model, or our own small model. */
export const TRANSLATE_ENGINES = ['whisper', 'gemini', 'local'] as const;
export type TranslateEngine = (typeof TRANSLATE_ENGINES)[number];
/** Where the speech or text came from: a file, a recording, a link, a transcript or captions. */
export const TRANSLATE_SOURCES = ['file', 'record', 'link', 'transcript', 'captions'] as const;
export type TranslateSource = (typeof TRANSLATE_SOURCES)[number];
/**
 * How a translation was asked for: the transcript page's one-tap "Translate to English" button
 * (SEO_BRIEF_09 section 6.0), or a language picker (the transcript page's Translate picker, and
 * the target picker on a translation tool page, lib/translate-target.ts). Always sent.
 */
export const TRANSLATE_TRIGGERS = ['en_button', 'picker'] as const;
export type TranslateTrigger = (typeof TRANSLATE_TRIGGERS)[number];
/** The caption generator's platforms (POST /api/transcripts/{id}/captions `platform`). */
export const CAPTION_PLATFORMS = [
  'tiktok',
  'instagram',
  'youtube_shorts',
  'x',
  'facebook',
] as const;
export type CaptionPlatform = (typeof CAPTION_PLATFORMS)[number];
/**
 * The language model's four features: a transcript translated, a subtitle file translated, a
 * summary, social captions.
 */
export const GEMINI_FEATURES = ['translate', 'subtitles', 'summary', 'captions'] as const;
export type GeminiFeature = (typeof GEMINI_FEATURES)[number];
/** Whose limit was met: everyone's for the day, or this visitor's. */
export const GEMINI_SCOPES = ['global', 'visitor'] as const;
export type GeminiScope = (typeof GEMINI_SCOPES)[number];
/** The live translator's two modes (SEO_BRIEF_10 section 2.2): one speaker, or two people. */
export const LIVE_TRANSLATE_MODES = ['speak', 'conversation'] as const;
export type LiveTranslateMode = (typeof LIVE_TRANSLATE_MODES)[number];
/**
 * What translated one live sentence: the browser's own translator on the device, the language
 * model (POST /api/live-translate), or Whisper large-v3 (Hold to talk: a recording uploaded).
 */
export const LIVE_TRANSLATE_ENGINES = ['device', 'gemini', 'whisper'] as const;
export type LiveTranslateEngine = (typeof LIVE_TRANSLATE_ENGINES)[number];
/**
 * The most sentences one `live_translate_sentence` beacon counts (its `sentences` field). The
 * Worker's LIVE_SENTENCE_BATCH is the same number and refuses a beacon over it.
 */
export const LIVE_SENTENCE_BATCH = 20;
/**
 * The homepage's three examples, in the buttons' order (lib/examples.ts): an English talk, a
 * Spanish clip translated into English, a YouTube video summarized. The Worker's EXAMPLE_NAMES
 * is the same list.
 */
export const EXAMPLE_NAMES = ['talk', 'spanish', 'youtube'] as const;
export type ExampleName = (typeof EXAMPLE_NAMES)[number];
/**
 * The three "how to transcribe" guides, in the brief's order (SEO_BRIEF_12 section 5): the pages
 * a `guide_open` can come from, by their slugs. The Worker's GUIDE_NAMES is the same list.
 */
export const GUIDE_NAMES = [
  'how-to-transcribe-audio',
  'how-to-transcribe-a-video',
  'how-to-transcribe-a-youtube-video',
] as const;
export type GuideName = (typeof GUIDE_NAMES)[number];
/** The event a tap from a guide to a tool page sends (lib/guide-links.ts), one of FIELD_EVENTS. */
export const GUIDE_OPEN_EVENT = 'guide_open' satisfies UsageEvent;

/**
 * A target language: a Whisper code ('en', 'es', 'haw', 'yue') or 'zh-Hant'. The Worker holds it
 * to Whisper's own list (server/pipeline/languages.py); this only keeps text out of the body.
 */
export const TARGET_CODE = /^(?:[a-z]{2,3}|zh-Hant)$/;
/** The live captions language: a BCP 47 tag from the picker, 'en-US', 'fil-PH', or a bare code. */
export const LIVE_LANG = /^[a-z]{2,3}(?:-[A-Z]{2})?$/;
/** More cues than any subtitle file the translator takes is not a count, it is garbage. */
export const MAX_CUES = 100_000;
/**
 * How a transcript page's link was shared (lib/share.ts): the device's own share sheet, Copy link,
 * or one of the menu's four links.
 */
export const SHARE_METHODS = ['native', 'copy', 'whatsapp', 'telegram', 'x', 'email'] as const;
export type ShareMethod = (typeof SHARE_METHODS)[number];
/** The event the rating pop-up sends (lib/rating-prompt.ts), one of FIELD_EVENTS. */
export const RATE_EVENT = 'rate' satisfies UsageEvent;
/** What the visitor had just done when they rated: copied a result, or downloaded one. */
export const RATE_AFTERS = ['copy', 'download'] as const;
export type RateAfter = (typeof RATE_AFTERS)[number];
/** The most stars a rating gives; the fewest is 1. The Worker's MAX_STARS is the same. */
export const MAX_STARS = 5;
/** A page path a rating names: a slash and up to 200 characters (lib/page-time.ts PAGE_PATH). */
export const RATE_PAGE = /^\/[^\s?#]{0,200}$/;

/** Each event's fields, as `countUse` takes them. */
export interface UsageFields {
  example: { which: ExampleName };
  translate_start: {
    target: string;
    engine: TranslateEngine;
    source: TranslateSource;
    trigger: TranslateTrigger;
  };
  translate_done: { target: string; engine: TranslateEngine };
  subtitle_translate: { target: string; cues: number; format: UsageFormat };
  summary_start: { source: TranslateSource };
  summary_cache_hit: Record<string, never>;
  caption_generate: { platform: CaptionPlatform };
  gemini_limit_hit: { feature: GeminiFeature; scope: GeminiScope };
  live_captions_start: { lang: string };
  share: { method: ShareMethod };
  guide_open: { guide: GuideName; tool: string };
  live_translate_start: { mode: LiveTranslateMode; source: string; target: string };
  live_translate_sentence: {
    engine: LiveTranslateEngine;
    source: string;
    target: string;
    sentences: number;
  };
  live_translate_limit: { scope: GeminiScope };
  live_translate_download: Record<string, never>;
  rate: { stars: number; after: RateAfter; page: string };
}
export type FieldEvent = keyof UsageFields;
/** The field events with no fields: `countUse` takes `{}` or nothing for them. */
export type EmptyFieldEvent = 'summary_cache_hit' | 'live_translate_download';

/** Each field event's fields, in the body's order. The Worker's FIELD_EVENTS is the same. */
export const FIELD_EVENTS: { readonly [E in FieldEvent]: readonly (keyof UsageFields[E])[] } = {
  example: ['which'],
  translate_start: ['target', 'engine', 'source', 'trigger'],
  translate_done: ['target', 'engine'],
  subtitle_translate: ['target', 'cues', 'format'],
  summary_start: ['source'],
  summary_cache_hit: [],
  caption_generate: ['platform'],
  gemini_limit_hit: ['feature', 'scope'],
  live_captions_start: ['lang'],
  share: ['method'],
  guide_open: ['guide', 'tool'],
  live_translate_start: ['mode', 'source', 'target'],
  live_translate_sentence: ['engine', 'source', 'target', 'sentences'],
  live_translate_limit: ['scope'],
  live_translate_download: [],
  rate: ['stars', 'after', 'page'],
};

const inList =
  (list: readonly string[]) =>
  (value: unknown): boolean =>
    typeof value === 'string' && list.includes(value);

const targetCode = (value: unknown): boolean =>
  typeof value === 'string' && TARGET_CODE.test(value);

/** How each field is checked. A field not here is never sent. */
const FIELD_CHECKS: Readonly<Record<string, (value: unknown) => boolean>> = {
  which: inList(EXAMPLE_NAMES),
  target: targetCode,
  engine: inList(TRANSLATE_ENGINES),
  source: inList(TRANSLATE_SOURCES),
  trigger: inList(TRANSLATE_TRIGGERS),
  cues: (v) => typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= MAX_CUES,
  format: inList(USAGE_FORMATS),
  platform: inList(CAPTION_PLATFORMS),
  feature: inList(GEMINI_FEATURES),
  scope: inList(GEMINI_SCOPES),
  lang: (v) => typeof v === 'string' && LIVE_LANG.test(v),
  mode: inList(LIVE_TRANSLATE_MODES),
  sentences: (v) =>
    typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= LIVE_SENTENCE_BATCH,
  stars: (v) => typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= MAX_STARS,
  after: inList(RATE_AFTERS),
  page: (v) => typeof v === 'string' && RATE_PAGE.test(v),
  method: inList(SHARE_METHODS),
  // A guide's (GUIDES above): which guide, and the tool page opened from it, by its slug. The
  // Worker also holds the slug to a page of the site (a Rule 1 path).
  guide: inList(GUIDE_NAMES),
  tool: (v) => typeof v === 'string' && TOOL_SLUG.test(v),
};

/**
 * The live translator's fields whose names other events use for something else (SEO_BRIEF_10
 * section 2.6): its `source` is a language, as its `target` is, and its `engine` is one of
 * LIVE_TRANSLATE_ENGINES. Read before FIELD_CHECKS, for these two events only.
 */
const LIVE_FIELD_CHECKS: Readonly<Record<string, (value: unknown) => boolean>> = {
  source: targetCode,
  engine: inList(LIVE_TRANSLATE_ENGINES),
};
const EVENT_FIELD_CHECKS: Partial<
  Record<FieldEvent, Readonly<Record<string, (value: unknown) => boolean>>>
> = {
  live_translate_start: LIVE_FIELD_CHECKS,
  live_translate_sentence: LIVE_FIELD_CHECKS,
};

/** True for an event that carries fields. */
export function isFieldEvent(event: unknown): event is FieldEvent {
  return typeof event === 'string' && Object.hasOwn(FIELD_EVENTS, event);
}

/**
 * `extra` as the body's `fields`, rebuilt key by key in FIELD_EVENTS' order, or null when it is
 * not exactly the event's fields with values on their lists. The two without fields
 * (`EmptyFieldEvent`) take `{}` or nothing.
 */
export function usageFields(
  event: FieldEvent,
  extra: unknown,
): Record<string, string | number> | null {
  const names = FIELD_EVENTS[event] as readonly string[];
  if (extra === undefined && names.length === 0) return {};
  if (typeof extra !== 'object' || extra === null || Array.isArray(extra)) return null;
  const given = extra as Record<string, unknown>;
  const keys = Object.keys(given);
  if (keys.length !== names.length || keys.some((k) => !names.includes(k))) return null;
  const fields: Record<string, string | number> = {};
  const own = EVENT_FIELD_CHECKS[event];
  for (const name of names) {
    const value = given[name];
    const check = own?.[name] ?? FIELD_CHECKS[name];
    if (check === undefined || !check(value)) return null;
    fields[name] = value as string | number;
  }
  return fields;
}

const EVENTS: ReadonlySet<string> = new Set(USAGE_EVENTS);
const FORMATS: ReadonlySet<string> = new Set(USAGE_FORMATS);
const HOWS: ReadonlySet<string> = new Set(COPY_HOWS);
/** A result taken away: what the homepage's "subtitle files converted" counts (`repeat`). */
const RESULT_EVENTS: ReadonlySet<string> = new Set(['downloaded', 'copied']);

/** A format name as the counter's list has it, and `unknown` when it is not on the list. */
export function usageFormat(format: unknown): UsageFormat {
  if (typeof format !== 'string') return 'unknown';
  const name = format.toLowerCase();
  // SAMI's other extension. The dropzone routes both as SMI.
  if (name === 'sami') return 'smi';
  return FORMATS.has(name) ? (name as UsageFormat) : 'unknown';
}

/** The video and audio extensions counted by name; every other one is `video` or `audio`. */
const MEDIA_FORMATS: ReadonlyMap<string, UsageFormat> = new Map<string, UsageFormat>([
  ['mp4', 'mp4'],
  ['m4v', 'mp4'],
  ['mov', 'mov'],
  ['mkv', 'mkv'],
  ['webm', 'webm'],
  ['mp3', 'mp3'],
  ['m4a', 'm4a'],
  ['wav', 'wav'],
]);

/**
 * A video or audio file as the counter's list has it, from its extension (the dropzone's
 * `extensionOf`) and what the dropzone routed it as. The four video containers and the three
 * audio formats the audio pages are named for are counted by name, any other as `video` or
 * `audio`. Only the list's value leaves this function, never the extension itself.
 */
export function mediaFormat(extension: string, kind: 'video' | 'audio'): UsageFormat {
  const named =
    typeof extension === 'string' ? MEDIA_FORMATS.get(extension.toLowerCase()) : undefined;
  return named ?? kind;
}

/**
 * A recording's format as the counter's list has it: WebM by name, Safari's MP4 audio as `m4a`
 * (what the download is called), and Ogg, which the list does not name, as `audio`.
 */
export function recordingFormat(extension: string): UsageFormat {
  if (extension === 'webm') return 'webm';
  if (extension === 'm4a') return 'm4a';
  return 'audio';
}

/**
 * The input format of a file that could not be read.
 *
 * The worker says when the file is a ZIP, audio or a video, and names the format it detected
 * when it got that far. Otherwise it is the format the file's extension routed it as (the
 * dropzone's `classify`), and `unknown` when that is not one of ours. Only the list's value
 * leaves this function, never the extension itself.
 */
export function failedFormat(extension: string, err: unknown): UsageFormat {
  const failure = typeof err === 'object' && err !== null ? (err as Record<string, unknown>) : {};
  const reason = failure['reason'];
  if (reason === 'is-archive') return 'zip';
  if (reason === 'is-audio' || reason === 'is-container') return 'unknown';
  const detail = failure['detail'];
  const detected =
    typeof detail === 'object' && detail !== null
      ? (detail as Record<string, unknown>)['format']
      : undefined;
  if (typeof detected === 'string') return usageFormat(detected);
  return usageFormat(extension);
}

/**
 * The beacon's body, or null when anything is outside the contract.
 *
 * Built field by field from the five values, never by spreading an object a caller passed, so
 * nothing else can ride along. A `copied` body's format is one of COPY_HOWS, every other's one
 * of USAGE_FORMATS. `encoding` is sent only as `euc-kr` and only on a download; UTF-8 is the
 * default and is left out. `repeat` is sent only as `true`, only on a download or a copy, and
 * says this page view's result is already in the homepage's total (`countUse`). An event of
 * FIELD_EVENTS needs its `extra` (`usageFields`), sent last as `fields`; every other event takes
 * none.
 */
export function usageBody(
  event: UsageEvent,
  tool: string,
  format: UsageFormat | CopyHow,
  encoding?: SaveEncoding,
  repeat?: boolean,
  extra?: unknown,
): string | null {
  if (typeof event !== 'string' || !EVENTS.has(event) || event === PAGE_TIME_EVENT) return null;
  if (typeof tool !== 'string' || !TOOL_SLUG.test(tool)) return null;
  const formats = event === 'copied' ? HOWS : FORMATS;
  if (typeof format !== 'string' || !formats.has(format)) return null;
  if (encoding !== undefined && encoding !== 'utf-8' && encoding !== 'euc-kr') return null;
  const eucKr = encoding === 'euc-kr';
  if (eucKr && event !== 'downloaded') return null;
  if (repeat !== undefined && repeat !== false && repeat !== true) return null;
  if (repeat === true && !RESULT_EVENTS.has(event)) return null;
  let fields: Record<string, string | number> | null = null;
  if (isFieldEvent(event)) {
    fields = usageFields(event, extra);
    if (fields === null) return null;
  } else if (extra !== undefined) {
    return null;
  }
  return JSON.stringify({
    event,
    tool,
    format,
    ...(eucKr ? { encoding: 'euc-kr' } : {}),
    ...(repeat === true ? { repeat: true } : {}),
    ...(fields === null ? {} : { fields }),
  });
}

/** True in a browser that asked not to be counted. Unreadable storage is not that ask. */
export function countingOff(): boolean {
  try {
    return localStorage.getItem(NO_STATS_FLAG) === '1';
  } catch {
    return false;
  }
}

/** Send one body. Skipped when this browser has the flag, has no beacon, or a value is off-list. */
function send(
  event: UsageEvent,
  tool: string,
  format: UsageFormat | CopyHow,
  encoding?: SaveEncoding,
  repeat?: boolean,
  extra?: unknown,
): void {
  if (countingOff()) return;
  const body = usageBody(event, tool, format, encoding, repeat, extra);
  if (body === null) return;
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
  navigator.sendBeacon(USAGE_ENDPOINT, body);
}

/** The events that take no fields: their fourth argument, when there is one, is the encoding. */
export type PlainEvent = Exclude<UsageEvent, FieldEvent>;

/**
 * Count one use. Returns nothing and throws nothing.
 *
 * `countUse(event, tool, format, extra?)`. For an event of FIELD_EVENTS, `extra` is its flat
 * fields object (`UsageFields`), required except on the two without fields (`EmptyFieldEvent`):
 *
 *   countUse('translate_start', 'audio-translator', 'mp4',
 *            {target: 'en', engine: 'whisper', source: 'file', trigger: 'picker'});
 *
 * For every other event the fourth argument is the download's encoding, as it always was.
 *
 * `sendBeacon` with a string body: a `text/plain` POST that outlives the page and never waits
 * on an answer. Skipped when this browser has the flag, when the browser has no beacon, or when
 * any value is outside the lists. A load, a download and a copy are also noted for this page
 * view (THE PAGE VIEW below), which is where a download or a copy gets its `repeat`.
 */
export function countUse(
  event: EmptyFieldEvent,
  tool: string,
  format: UsageFormat,
  extra?: Record<string, never>,
): void;
export function countUse<E extends FieldEvent>(
  event: E,
  tool: string,
  format: UsageFormat,
  extra: UsageFields[E],
): void;
export function countUse(
  event: PlainEvent,
  tool: string,
  format: UsageFormat | CopyHow,
  encoding?: SaveEncoding,
): void;
export function countUse(
  event: UsageEvent,
  tool: string,
  format: UsageFormat | CopyHow,
  extra?: SaveEncoding | object,
): void {
  try {
    if (isFieldEvent(event)) {
      send(event, tool, format, undefined, undefined, extra);
      tellResult(event, tool);
      return;
    }
    // Anything but an encoding here is refused by `usageBody`, as an unknown encoding always was.
    const repeat = noteOutcome(event, tool, format);
    send(event, tool, format, extra as SaveEncoding | undefined, repeat);
    tellResult(event, tool);
  } catch {
    /* counting never surfaces */
  }
}

/* ── RESULTS TAKEN AWAY ──────────────────────────────────────────────────
 *
 * The rating pop-up (lib/rating-prompt.ts) waits for a visitor's first copy or download in a page
 * view. Rather than every tool calling it, it listens here, where every one is already counted:
 * these events, each as what the visitor did. Told after the beacon, and told even in a browser
 * that is not counted; a listener that throws is ignored. A copy or a download with no beacon of
 * its own (a transcript page's summary and captions, live captions' save, the live translator's
 * Copy) is told through `noteResult`, which sends nothing.
 *
 * The listeners belong to the page, not to this module: audio-out.js and burn.js each bundle
 * their own copy of it (esbuild does not split), and the one card boot.js starts must hear their
 * downloads too. So the list is kept on the global object under a registry symbol
 * (RESULT_LISTENERS), the same list for every copy on the page.
 */

/** The events that are a result taken away, as the rating's `after`. */
export const RESULT_AFTER: Readonly<Partial<Record<PlainEvent, RateAfter>>> = {
  downloaded: 'download',
  copied: 'copy',
  record_download: 'download',
  live_copy: 'copy',
  live_save: 'download',
  pdf_text_download: 'download',
};
/** The live translator's saved session, a field event, is a download too. */
const FIELD_RESULT_AFTER: Readonly<Partial<Record<FieldEvent, RateAfter>>> = {
  live_translate_download: 'download',
};

type ResultListener = (tool: string, after: RateAfter) => void;
/** Where the page keeps its listeners, shared by every bundle's copy of this module. */
export const RESULT_LISTENERS = Symbol.for('transcriptworks.resultListeners');

function listenersOf(): ResultListener[] {
  const shared = globalThis as unknown as Record<symbol, ResultListener[] | undefined>;
  let list = shared[RESULT_LISTENERS];
  if (!Array.isArray(list)) {
    list = [];
    shared[RESULT_LISTENERS] = list;
  }
  return list;
}

/** Hear every result taken away: a copy or a download, with its tool. Returns the way to stop. */
export function onResult(listener: ResultListener): () => void {
  const listeners = listenersOf();
  listeners.push(listener);
  return () => {
    const at = listeners.indexOf(listener);
    if (at >= 0) listeners.splice(at, 1);
  };
}

/**
 * Tell the listeners of a copy or a download that is not counted (no beacon goes): the visitor
 * took a result away all the same. Never throws.
 */
export function noteResult(tool: string, after: RateAfter): void {
  try {
    tellAfter(tool, after);
  } catch {
    /* a result never surfaces */
  }
}

function tellResult(event: UsageEvent, tool: string): void {
  const after = isFieldEvent(event) ? FIELD_RESULT_AFTER[event] : RESULT_AFTER[event as PlainEvent];
  if (after !== undefined) tellAfter(tool, after);
}

function tellAfter(tool: string, after: RateAfter): void {
  if (typeof tool !== 'string' || !TOOL_SLUG.test(tool)) return;
  if (!(RATE_AFTERS as readonly string[]).includes(after)) return;
  for (const listener of [...listenersOf()]) {
    try {
      listener(tool, after);
    } catch {
      /* a listener never surfaces */
    }
  }
}

/* ── THE PAGE VIEW ───────────────────────────────────────────────────────
 *
 * What each tool did in this page view, in memory and nowhere else, gone with the page:
 *
 *   * NO RESULT. A tool that had a file loaded and gave nothing back, no download, no copy and no
 *     result reached another way (`resultReached`), sends one `no_result` when the page is left
 *     (`pagehide`), with the format of the last file loaded. A file that failed to load is
 *     already counted as `failed` and is not a no-result. Not on `visibilitychange`: a visitor
 *     who switches tabs and comes back to download has not given up. A mobile browser that
 *     discards a background tab without `pagehide` sends nothing, so this undercounts.
 *   * ONE RESULT, ONE CONVERSION. The homepage's "subtitle files converted" (the Worker's
 *     totals.js) adds a download or a copy of a conversion tool's result. A file loaded starts a
 *     new result. A copy after anything already counted for that result, or a download after a
 *     copy of it, goes out with `repeat`, and the Worker leaves the total alone; the day's
 *     counts take it as usual. A second download after a download still adds, as every
 *     download always has.
 */

interface ToolOutcome {
  /** The format of the last file loaded in this page view, or null before one. */
  loaded: UsageFormat | null;
  /** A result was downloaded, copied or reached another way in this page view. */
  resulted: boolean;
  /** What of the current result went into the homepage's total first, if anything did. */
  tallied: 'downloaded' | 'copied' | null;
  /** Files loaded so far: a copy watcher (lib/copies.ts) counts a new file's result again. */
  loads: number;
}

const outcomes = new Map<string, ToolOutcome>();
let watchingPageEnd = false;

function outcomeOf(tool: string): ToolOutcome {
  let outcome = outcomes.get(tool);
  if (outcome === undefined) {
    outcome = { loaded: null, resulted: false, tallied: null, loads: 0 };
    outcomes.set(tool, outcome);
  }
  return outcome;
}

/** Note a load, a download or a copy; true when a download or copy is a `repeat`. */
function noteOutcome(event: UsageEvent, tool: string, format: UsageFormat | CopyHow): boolean {
  if (event === 'loaded') {
    const outcome = outcomeOf(tool);
    outcome.loaded = FORMATS.has(format) ? (format as UsageFormat) : 'unknown';
    outcome.tallied = null;
    outcome.loads += 1;
    watchPageEnd();
    return false;
  }
  if (!RESULT_EVENTS.has(event)) return false;
  const outcome = outcomeOf(tool);
  outcome.resulted = true;
  const first = outcome.tallied;
  if (first === null) outcome.tallied = event as 'downloaded' | 'copied';
  return event === 'copied' ? first !== null : first === 'copied';
}

function watchPageEnd(): void {
  if (watchingPageEnd) return;
  watchingPageEnd = true;
  if (typeof globalThis.addEventListener === 'function') {
    globalThis.addEventListener('pagehide', endPageView);
  }
}

/** How many files this page view has loaded into `tool`, for lib/copies.ts. */
export function loadsOf(tool: string): number {
  return outcomes.get(tool)?.loads ?? 0;
}

/**
 * This page view got `tool`'s result without a download or a copy, so leaving it is no
 * `no_result`: a transcription the server took (the job page and the transcript page carry on
 * from there), or a report read on the page (/subtitle-reading-speed-checker).
 */
export function resultReached(tool: string): void {
  try {
    outcomeOf(tool).resulted = true;
  } catch {
    /* counting never surfaces */
  }
}

/**
 * The page is being left (`pagehide`): one `no_result` for each tool with a file loaded and no
 * result, then the page view is forgotten, so a second `pagehide` sends nothing. A page brought
 * back from the back-forward cache starts a new one. Throws nothing.
 */
export function endPageView(): void {
  for (const [tool, outcome] of outcomes) {
    try {
      if (outcome.loaded !== null && !outcome.resulted) send('no_result', tool, outcome.loaded);
    } catch {
      /* counting never surfaces */
    }
  }
  outcomes.clear();
}

/** One tool page's counter. */
export interface UsageCounter {
  /** A file was read and parsed. `format` is the one detected. */
  loaded(file: object, format: unknown): void;
  /** A file could not be read or parsed. */
  failed(file: object, format: unknown): void;
  /** A result was saved, in `format` and `encoding`. Every click counts. */
  downloaded(format: unknown, encoding?: SaveEncoding): void;
}

/**
 * The counter a tool page holds, for its slug.
 *
 * A FILE COUNTS ONCE, as loaded or as failed, however often the page parses it again. The
 * MicroDVD frame-rate answer parses the same `File` again, and each new answer would otherwise
 * count it again. Option and encoding changes re-run without parsing, so they never reach here.
 * A file dropped again is a new `File`, and counts again.
 */
export function usageCounter(tool: string): UsageCounter {
  const counted = new WeakSet<object>();
  const once = (event: 'loaded' | 'failed', file: object, format: unknown): void => {
    try {
      if (counted.has(file)) return;
      counted.add(file);
    } catch {
      return;
    }
    countUse(event, tool, usageFormat(format));
  };
  return {
    loaded: (file, format) => once('loaded', file, format),
    failed: (file, format) => once('failed', file, format),
    downloaded: (format, encoding) => countUse('downloaded', tool, usageFormat(format), encoding),
  };
}
